import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Message,
  Select,
  Space,
  Typography,
} from "@arco-design/web-react";
import { getMe, testPat, testRobot, updateMe, type InstallationMetadata } from "../api/installations.js";
import { getCatalog, type PlatformManifest } from "../api/platforms.js";
import { useAuthStore } from "../store/authStore.js";
import { parseApiError } from "../api/http.js";

const { Title, Text, Paragraph } = Typography;

export function SettingsPage(): JSX.Element {
  const auth = useAuthStore();
  const [meta, setMeta] = useState<InstallationMetadata | null>(null);
  const [catalog, setCatalog] = useState<PlatformManifest[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [enabledPlatforms, setEnabledPlatforms] = useState<string[]>([]);
  const [status, setStatus] = useState<"active" | "paused" | "disabled">("active");
  const [newPat, setNewPat] = useState("");
  const [newRobot, setNewRobot] = useState("");
  const [saving, setSaving] = useState(false);
  const [patTesting, setPatTesting] = useState(false);
  const [robotTesting, setRobotTesting] = useState(false);

  useEffect(() => {
    Promise.all([getMe(), getCatalog()])
      .then(([m, c]) => {
        setMeta(m);
        setCatalog(c.platforms);
        setDisplayName(m.display_name);
        setEnabledPlatforms(m.enabled_platforms);
        setStatus(m.status);
      })
      .catch((err: unknown) => Message.error(parseApiError(err).message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const updated = await updateMe({
        display_name: displayName,
        enabled_platforms: enabledPlatforms,
        status,
        ...(newPat && { pat: newPat }),
        ...(newRobot && { robot_webhook: newRobot }),
      });
      setMeta(updated);
      auth.setSession({
        installId: updated.install_id,
        sessionToken: auth.sessionToken ?? "",
        displayName: updated.display_name,
        enabledPlatforms: updated.enabled_platforms,
        hasPat: updated.has_pat,
        hasRobotWebhook: updated.has_robot_webhook,
      });
      setNewPat("");
      setNewRobot("");
      Message.success("已保存");
    } catch (err) {
      Message.error(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestPat = async (): Promise<void> => {
    if (!newPat) {
      Message.warning("请先填入新的 PAT");
      return;
    }
    if (!meta) return;
    setPatTesting(true);
    try {
      const res = await testPat({ pat: newPat, table_id: meta.feishu_table_id });
      if (res.ok) {
        Message.success(`PAT 有效${res.total !== undefined ? `（订单表当前 ${res.total} 条记录）` : ""}`);
      } else {
        Message.error(`PAT 无效：${res.reason ?? ""}`);
      }
    } finally {
      setPatTesting(false);
    }
  };

  const handleTestRobot = async (): Promise<void> => {
    setRobotTesting(true);
    try {
      const res = await testRobot();
      if (res.ok) {
        Message.success(`已发送测试消息${res.message_id ? `（message_id=${res.message_id}）` : ""}`);
      } else {
        Message.error(`发送失败：${res.reason ?? ""}`);
      }
    } catch (err) {
      Message.error(parseApiError(err).message);
    } finally {
      setRobotTesting(false);
    }
  };

  if (loading || !meta) return <Text type="secondary">加载中…</Text>;

  return (
    <div className="os-stack" style={{ maxWidth: 720 }}>
      <Title heading={5}>安装设置</Title>

      <div className="os-card os-stack">
        <Title heading={6} style={{ margin: 0 }}>
          基础
        </Title>
        <div>
          <Text bold>install_id</Text>
          <div>
            <code>{meta.install_id}</code>
          </div>
        </div>
        <div>
          <Text bold>显示名</Text>
          <Input style={{ marginTop: 6 }} value={displayName} onChange={setDisplayName} maxLength={50} />
        </div>
        <div>
          <Text bold>状态</Text>
          <Select
            style={{ width: 200, marginTop: 6 }}
            value={status}
            onChange={(v) => setStatus(v as typeof status)}
            options={[
              { label: "运行中", value: "active" },
              { label: "已暂停", value: "paused" },
              { label: "已禁用", value: "disabled" },
            ]}
          />
        </div>
      </div>

      <div className="os-card os-stack">
        <Title heading={6} style={{ margin: 0 }}>
          启用的平台
        </Title>
        <Checkbox.Group value={enabledPlatforms} onChange={(v) => setEnabledPlatforms(v as string[])}>
          {catalog.map((p) => (
            <Checkbox key={p.platform} value={p.platform} style={{ display: "block", marginBottom: 6 }}>
              {p.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>

      <div className="os-card os-stack">
        <Title heading={6} style={{ margin: 0 }}>
          飞书 PAT
        </Title>
        <Alert
          type={meta.has_pat ? "success" : "warning"}
          content={meta.has_pat ? "已配置（出于安全考虑不显示原值）" : "未配置 — 业务不能运行"}
        />
        <Paragraph>
          需要更换 PAT 时在下面填新值。留空则不修改。
        </Paragraph>
        <Input.Password value={newPat} onChange={setNewPat} placeholder="新 PAT（留空保留原值）" />
        <Space>
          <Button onClick={handleTestPat} loading={patTesting} disabled={!newPat}>
            用新值测试连通
          </Button>
        </Space>
      </div>

      <div className="os-card os-stack">
        <Title heading={6} style={{ margin: 0 }}>
          群机器人 Webhook
        </Title>
        <Alert
          type={meta.has_robot_webhook ? "success" : "info"}
          content={meta.has_robot_webhook ? "已配置" : "未配置 — 群提醒将被跳过"}
        />
        <Input
          placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/..."
          value={newRobot}
          onChange={setNewRobot}
        />
        <Space>
          <Button onClick={handleTestRobot} loading={robotTesting} disabled={!meta.has_robot_webhook}>
            发测试消息
          </Button>
        </Space>
      </div>

      <div className="os-row" style={{ justifyContent: "flex-end" }}>
        <Button type="primary" onClick={handleSave} loading={saving}>
          保存
        </Button>
      </div>
    </div>
  );
}
